import { action, makeAutoObservable } from "mobx";
import INTOUCH_API from "../services/Api";
import { CURRENT_GAME_KEY } from "../utils/constants";
import PersonalAccount from "./PersonalAccount";
import { createDefaultZones } from "./zonesConfig";
import * as Sentry from "@sentry/react";

const END_GAME_NOTIFICATION = "END_GAME_NOTIFICATION";

class Game {
  game = null;
  currentGameInsightEventData = null;
  cards = [];
  cardsSetType = null;
  isZonesLoading = false;
  // 21.09: зоны принадлежат КОНКРЕТНОЙ игре. Раньше это нигде не фиксировалось, и
  // экран выбора темы, который теперь читает зоны, мог отрисоваться по карточкам
  // предыдущей игры либо решить «карточек нет», пока запрос ещё не ушёл.
  zonesGameId = null;
  // 24.09: отличаем «карточек правда нет» (бэк отдаёт 400, пока тест прошли не
  // оба) от сбоя загрузки — во втором случае пользователю нельзя показывать
  // «карточки появятся, когда вы оба пройдёте тест», это враньё
  zonesLoadFailed = false;
  // номер запроса: ответ по прошлой игре не должен перетирать зоны текущей
  zonesSeq = 0;

  gameHistory = [];
  lastGameAnswers = [];

  countNewMatches = null;
  compromiseMatches = [];

  insightEvents = [];
  endGameEvent = [];

  countMatches = 0;

  matches = {
    negative: [],
    positive: [],
    neutral: [],
  };
  all_matches = [];

  zones = createDefaultZones();

  constructor() {
    makeAutoObservable(this);
    const current_game = localStorage.getItem(CURRENT_GAME_KEY);
    if (current_game === "undefined") {
      localStorage.removeItem(CURRENT_GAME_KEY);
    }

    if (current_game && current_game !== "undefined") {
      const game = JSON.parse(current_game);
      if (game) {
        this.setGame(game);
      }
    }
  }

  getGame() {
    return this.game;
  }

  getInsightEvent(gameId, userId) {
    const foundInsightEvent = this.insightEvents.find(
      (el) => el.gameId === gameId && el.userId === userId && el.count > 0
    );
    return foundInsightEvent || null;
  }

  getAllInsightEvent(userId) {
    const insightEventCount = this.insightEvents.reduce((acc, current) => {
      if (current.userId === userId && current.count > 0) {
        acc = acc + current.count;
      }
      return acc;
    }, 0);

    return insightEventCount || 0;
  }

  getEndGameNotificationCount(gameId, userId) {
    const foundEndGameEvent = this.endGameEvent.filter(
      (el) =>
        el.gameId === gameId &&
        el.userId === userId &&
        !el.isViewed &&
        el.event === END_GAME_NOTIFICATION
    );
    return foundEndGameEvent && foundEndGameEvent.length
      ? foundEndGameEvent.length
      : 0;
  }

  getAllEndGameNotificationCount(userId) {
    const foundAllEndGameEvent = this.endGameEvent.filter(
      (el) =>
        el.userId === userId &&
        !el.isViewed &&
        el.event === END_GAME_NOTIFICATION
    );
    return foundAllEndGameEvent && foundAllEndGameEvent.length
      ? foundAllEndGameEvent.length
      : 0;
  }

  setGameJoin(game) {
    this.game = game;
  }

  setGame(game) {
    this.game = game;
    this.gameHistory = [];
    this.lastGameAnswers = [];
    // сменилась игра — старые зоны больше не про неё
    if (this.zonesGameId !== null && game?.id !== this.zonesGameId) {
      this.zones = createDefaultZones();
      this.zonesGameId = null;
    }
  }

  setInsightEventsData(data) {
    this.insightEvents = data.allGamesInsightEventData;
    this.currentGameInsightEventData = this.game
      ? data.allGamesInsightEventData.find((el) => el.gameId === this.game.id)
      : null;
  }

  removeInsightEventsData(gameId) {
    this.insightEvents = this.insightEvents.filter(
      (event) => event.gameId !== gameId
    );

    this.currentGameInsightEventData =
      this.currentGameInsightEventData &&
      this.currentGameInsightEventData.gameId === gameId
        ? null
        : this.currentGameInsightEventData;
  }

  setGameEventsData(data, type = null) {
    if (type && type === "socket") {
      const existingIds = new Set(this.endGameEvent.map((item) => item.id));
      const uniqueNewData = data.filter((item) => !existingIds.has(item.id));
      this.endGameEvent = [...this.endGameEvent, ...uniqueNewData];
      return;
    }

    this.endGameEvent = data;
  }

  setCompromiseMatches(matches) {
    this.compromiseMatches = matches;
  }
  setCountCompromiseMatches(count) {
    this.countNewMatches = count;
  }

  setMatches(matches) {
    this.matches = matches;
  }

  async getZones(gameId) {
    const seq = ++this.zonesSeq;
    this.isZonesLoading = true;
    this.zonesLoadFailed = false;
    INTOUCH_API.requestManager(
      "getZones",
      { gameId },
      action((response) => {
        if (seq !== this.zonesSeq) return;
        this.zones = this.zones.map((item) => {
          return {
            ...item,
            cards: (response.data[item.key] || [])
              .map((card) => ({
                ...card,
                zoneKey: item.key,
              }))
              .sort((a, b) => (a.questionId ?? 0) - (b.questionId ?? 0)),
          };
        });
      }),
      action((error) => {
        if (seq !== this.zonesSeq) return;
        this.zones = this.zones.map((zone) => ({ ...zone, cards: [] }));
        this.zonesLoadFailed = error?.response?.status !== 400;
      }),
      action(() => {
        if (seq !== this.zonesSeq) return;
        this.isZonesLoading = false;
        // помечаем, для какой игры зоны загружены — и после ошибки тоже, иначе
        // потребитель будет бесконечно перезапрашивать
        this.zonesGameId = gameId;
      })
    );
  }

  showMore(zoneKey, val) {
    this.zones = this.zones.map((zone) => ({
      ...zone,
      isShowMore: zone.key === zoneKey ? val : zone.isShowMore,
    }));
  }

  async getNewMatches(gameId) {
    await INTOUCH_API.requestManager(
      "get_compromise_matches",
      { gameId },
      action((response) => {
        this.setCountCompromiseMatches(response.data.length);
        this.setCompromiseMatches(response.data);
      }),
      action((error) => {
        this.setCountCompromiseMatches(null);
        this.setCompromiseMatches([]);
      })
    );
  }

  async getAllGamesMatches() {
    await INTOUCH_API.requestManager(
      "all_games_coincidences",
      {},
      action((response) => {
        this.all_matches = response.data;
      }),
      action(() => {})
    );
  }

  markCheckedZoneItem(checkedCard) {
    this.zones = this.zones.map((zone) => {
      if (checkedCard.zoneKey === zone.key) {
        return {
          ...zone,
          cards: zone.cards.map((card) => {
            if (
              card.isCheckedZone &&
              card.questionId === checkedCard.questionId &&
              !checkedCard.isChecked
            ) {
              return {
                ...card,
                isCheckedZone: false,
              };
            } else if (
              !card.isCheckedZone &&
              card.questionId === checkedCard.questionId &&
              checkedCard.isChecked
            ) {
              return {
                ...card,
                isCheckedZone: true,
              };
            } else {
              return card;
            }
          }),
        };
      }
      return zone;
    });
  }

  async getCards(api, payload, onSuccess, onFinally) {
    await INTOUCH_API.requestManager(
      api,
      payload,
      action((response) => {
        this.cards = response.data;
        // какой набор загружен: свайпер +57 сверяет и перезагружает колоду,
        // если в cards осталась база (например, после захода в ЛК)
        this.cardsSetType = payload?.setType || "BASE";
        onSuccess && onSuccess(response);
      }),
      action((error) => {}),
      action(() => {
        onFinally && onFinally();
      })
    );
  }
  async getGameHistory(payload) {
    await INTOUCH_API.requestManager(
      "getHistoryGame",
      payload,
      action((response) => {
        this.setGameHistory(response.data.rows);
      })
    );
  }
  setGameHistory(gameHistory) {
    this.gameHistory = gameHistory;
  }

  async checkLastGameAnswers(data) {
    await INTOUCH_API.requestManager("getUserAnswers", data, (res) => {
      if (res && res?.data?.length) {
        this.setGameLastAnswers(res.data);
      }
    });
  }
  setGameLastAnswers(lastAnswers) {
    this.lastGameAnswers = lastAnswers;
  }

  async createNewGame(userId, onSuccess) {
    const demoForm = {
      type: "DEMO",
      userId,
    };

    return await INTOUCH_API.requestManager(
      "createDemoGame",
      demoForm,
      (response) => {
        this.setGame(response.data);
        PersonalAccount.games.push(response.data);
        onSuccess && onSuccess(response.data);
      },
      (error) => {
        // Обновление 16.07: продажа повторных тестов отменена — тесты создаются
        // без ограничений, гейта FREE_TEST_ALREADY_USED на бэке больше нет
        console.log(error);
        Sentry.captureException(error, {
          tags: {
            feature: "create_demo_game",
            component: "Game",
          },
          level: "warning",
        });
      }
    );
  }

  countPartnerGameMatches(gameId) {
    return this.all_matches.find((gm) => gm.gameId === gameId);
  }

  removeCards() {
    this.cards = [];
    this.cardsSetType = null;
  }

  setInitialState() {
    this.game = null;
    this.currentGameInsightEventData = null;
    this.cards = [];
    this.cardsSetType = null;
    this.isZonesLoading = false;
    this.zonesGameId = null;
    this.zonesLoadFailed = false;

    this.countNewMatches = null;
    this.compromiseMatches = [];

    this.insightEvents = [];

    this.countMatches = 0;

    this.matches = {
      negative: [],
      positive: [],
      neutral: [],
    };
    this.all_matches = [];

    this.zones = createDefaultZones();
  }
}

export default new Game();
